import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth';
import DOMPurify from 'dompurify';
import config from "../config";
import LoadingSkeletonRouteList from './LoadingSkeletonRouteList';
import BackButton from './BackButton';
import { calculateRouteDistance } from './routeUtils';
import useTranslation from './useTranslation';
import LocalIcon from './LocalIcon';


const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

const RoutesList = () => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await authService.get(config.API_ENDPOINTS.getPercorsiList(selectedLang));
        const routesData = response;
        
        // Ottimizzazione: Calcoliamo le distanze solo per i percorsi che non ce l'hanno già
        const routesNeedingDistances = routesData.filter(route => 
          !route.field_distanza?.[0]?.value && route.field_poi?.length > 1
        );

        if (routesNeedingDistances.length > 0) {
          // Fetch POIs in batch per tutti i percorsi che necessitano calcolo
          const allPoiIds = [...new Set(
            routesNeedingDistances.flatMap(route => 
              route.field_poi.map(poi => poi.target_id)
            )
          )];

          const poisPromises = allPoiIds.map(poiId =>
            authService.get(config.API_ENDPOINTS.getNode(poiId, selectedLang))
          );

          const poisResponses = await Promise.all(poisPromises);
          const poisMap = Object.fromEntries(
            poisResponses.map(response => [response.nid[0].value, response])
          );

          // Calcola le distanze per ogni percorso
          const updatedRoutes = await Promise.all(
            routesData.map(async (route) => {
              if (!route.field_distanza?.[0]?.value && route.field_poi?.length > 1) {
                const routePois = route.field_poi
                  .map(poi => poisMap[poi.target_id])
                  .filter(Boolean);

                try {
                  const result = await calculateRouteDistance(routePois);
                  if (result) {
                    return {
                      ...route,
                      field_distanza: [{ value: result.totalDistance }]
                    };
                  }
                } catch (error) {
                  console.error('Error calculating distance for route:', route.nid[0].value, error);
                }
              }
              return route;
            })
          );

          setRoutes(updatedRoutes.sort((a, b) => {
            // Ordina per field_posizione (se presente)
            const posA = a.field_posizione?.[0]?.value ? parseInt(a.field_posizione[0].value) : Number.MAX_SAFE_INTEGER;
            const posB = b.field_posizione?.[0]?.value ? parseInt(b.field_posizione[0].value) : Number.MAX_SAFE_INTEGER;
            
            // Se le posizioni sono diverse, ordina per posizione
            if (posA !== posB) return posA - posB;
            
            // Se le posizioni sono uguali, ordina alfabeticamente per titolo
            return a.title[0].value.localeCompare(b.title[0].value);
          }));
        } else {
          setRoutes(routesData.sort((a, b) => {
            // Ordina per field_posizione (se presente)
            const posA = a.field_posizione?.[0]?.value ? parseInt(a.field_posizione[0].value) : Number.MAX_SAFE_INTEGER;
            const posB = b.field_posizione?.[0]?.value ? parseInt(b.field_posizione[0].value) : Number.MAX_SAFE_INTEGER;
            
            // Se le posizioni sono diverse, ordina per posizione
            if (posA !== posB) return posA - posB;
            
            // Se le posizioni sono uguali, ordina alfabeticamente per titolo
            return a.title[0].value.localeCompare(b.title[0].value);
          }));
        }

        setLoading(false);
      } catch (error) {
        console.error('Errore durante il caricamento dei percorsi:', error);
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [selectedLang]);

  if (loading) {
    return <LoadingSkeletonRouteList />;
  }

  return (
    <main>
            
      <div className="container">
        <section className="mt-2 mb-4">
          <h2>{t('percorsi')}</h2>
          <p>{t('scopriItinerari')}</p>
        </section>
      

      
        <div className="gy-2">
          {routes.map((route) => (
            <div key={route.nid[0].value} className="mb-3">
              <div className="position-relative shadow-sm rounded overflow-hidden">
                <div className="position-relative" style={{/* minHeight: '300px' */}}>
                {route.field_immagine_percorso && route.field_immagine_percorso[0] && (
  <img
    src={route.field_immagine_percorso[0].url}
    alt={route.field_immagine_percorso[0].alt || route.title[0].value}
    className="w-100 h-100 position-absolute"
    loading="lazy"
    decoding="async"
    style={{ 
      objectFit: 'cover',
      top: 0,
      left: 0
    }}
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = '/placeholder-image.jpg'; // Assicurati di avere un'immagine placeholder
    }}
  />
)}
                  <div 
                    className="position-absolute w-100 h-100" 
                    style={{ 
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))',
                      top: 0,
                      left: 0
                    }}
                  />
                  
                  <div className="position-relative p-4 text-white text-start" style={{ zIndex: 2 }}>
                    <div className="d-flex justify-content-between mb-3 flex-fill">
                      {route.field_tempi_medi_di_percorrenza?.[0]?.processed && (
                        <span className="text-white small d-flex align-items-center">
                          <Icon name="schedule" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                          {DOMPurify.sanitize(route.field_tempi_medi_di_percorrenza[0].processed, { ALLOWED_TAGS: [] })}
                        </span>
                      )}
                      <span className="text-white small d-flex align-items-center">
                        <Icon name="signpost" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                        {route.field_poi?.length || 0} {t('tappe')}
                      </span>
                      {route.field_distanza?.[0]?.value && (
                        <span className="text-white small d-flex align-items-center">
                          <Icon name="near_me" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                          {route.field_distanza[0].value}
                        </span>
                      )}
                    </div>

                    <h5 className="fw-bold mb-2">
                      {route.title[0].value}
                    </h5>

                    

{route.field_descrizione_breve && route.field_descrizione_breve[0] && (
  <div 
    className="mb-3 text-white"
    style={{ 
      fontSize: '1rem'
    }}
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(
        route.field_descrizione_breve[0].value.replace(/<p>/g, '')
          .replace(/<\/p>/g, '')
          .substring(0, 160) + (route.field_descrizione_breve[0].value.length > 160 ? '...' : ''),
      )
    }}
  />
)}
                                                                                                                                                                
                    <div className="text-start">
                      <button
                        onClick={() => navigate(`/routes/${route.nid[0].value}`)}
                        className="btn bg-white border rounded-pill py-1 px-3 d-flex align-items-center shadow-sm"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {t('scopriPercorso')}
                        <Icon name="chevron_right" className="ms-1" style={{ fontSize: '1.1rem' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default RoutesList;